import { FriendlyNameService } from '../public/types/PanoramaArrowIdentifier';
import { AssertionException } from './utils/Exceptions';
import { DeviceKrpano } from './DeviceKrpano';
import { createGuid } from '../public/utils/Guid';
export const embedPanoPromised = (config, embedpano) => {
    return new Promise((resolve) => {
        embedpano({
            ...config,
            onready: (pano) => {
                resolve(pano);
            },
        });
    });
};
export class KrpanoWrapper {
    get Device() {
        return this._device;
    }
    get globalObj() {
        if (!this._krpanoGlobalObj) {
            this._krpanoGlobalObj = this._krpano.get('global');
        }
        return this._krpanoGlobalObj;
    }
    get actions() {
        return this.globalObj.actions;
    }
    get mouse() {
        return this.globalObj.mouse;
    }
    constructor(_krpano) {
        this._krpano = _krpano;
        this._krpanoGlobalObj = null;
        // NEW API BEGIN
        this.addKrpanoHotspot = (name) => {
            return this.globalObj.addhotspot(name);
        };
        this.getKrpanoHotspot = (name) => {
            const query = "hotspot['" + name + "']";
            return this.get(query);
        };
        this.unload = () => {
            this._krpano.unload();
        };
        // NEW API END
        this.get = (code) => this._krpano.get(code);
        //all pano set should be removed, it's API, and all this API specific things should on one sensible place, not necessarily in one file but on place
        this.set = (variable, value) => this._krpano.set(variable, value);
        //TODO: fix html type, was any now is string
        this.setHtml = (layerName, html) => this._krpano.set(`layer[${layerName}].html`, html);
        this.call = (action) => this._krpano.call(action);
        /** Translates X,Y from the screen/browser to ATH,ATV coordinates in panorama */
        this.screentosphere = (x, y) => this._krpano.screentosphere(x, y);
        this.spheretoscreen = (x, y) => this._krpano.spheretoscreen(x, y);
        this.getIntegerValue = (variable, returnIfNan) => {
            const value = parseInt(this._krpano.get(variable), 10);
            if (isNaN(value)) {
                if (returnIfNan !== undefined) {
                    return returnIfNan;
                }
                throw AssertionException('Expected number');
            }
            return value;
        };
        this.getStageWidth = () => this._krpano.get('stageWidth');
        this.getFloatValue = (variable) => {
            const value = parseFloat(this._krpano.get(variable));
            if (isNaN(value)) {
                throw AssertionException('Expected number');
            }
            return value;
        };
        this.addHotspot = (name) => {
            if (!/[a-zA-Z]/.test(name.charAt(0))) {
                throw AssertionException('Hotspot name must start with alphabetic character.');
            }
            this._krpano.call(`addhotspot(${name})`);
            const hotspot = this.getHotspot(name);
            if (hotspot) {
                return hotspot;
            }
            throw AssertionException('Hotspot must exist here.');
        };
        this.addLayer = (name) => {
            if (!/[a-zA-Z]/.test(name.charAt(0))) {
                throw AssertionException('Layer name must start with alphabetic character.');
            }
            this._krpano.call(`addlayer(${name})`);
            const layer = this.getLayer(name);
            if (layer) {
                return layer;
            }
            throw AssertionException('Hotspot must exist here.');
        };
        this.getHotspot = (name) => {
            const query = "hotspot['" + name + "']";
            return this._krpano.get(query);
        };
        this.getLayer = (name) => {
            const query = "layer['" + name + "']";
            return this._krpano.get(query);
        };
        this.registerOnClick = (handler) => this._krpano.set('events.onclick', handler);
        this.unregisterOnClick = () => this._krpano.set('events.onclick', null);
        this.registerOnNewScene = (handler) => this._krpano.set('events.onnewscene', handler);
        this.unregisterOnNewScene = () => this._krpano.set('events.onnewscene', null);
        this.loadEmptyPano = () => {
            const defaultXml = '<?xml version="1.0"?><krpano><scene><preview type="grid(cube,64,64,1024,0x232d33,0x0d1010,0xfdc23b);"/><view hlookat="0" vlookat="0" fovtype="HFOV" fisheye="0" fovmin="60" fov="130" fovmax="150"/></scene></krpano>';
            return this.loadPanoFromXmlString({
                baseDir: '',
                startingSceneIndex: 0,
                urlSuffix: '',
                xmlDefinition: defaultXml,
            }, false);
        };
        this.loadPanoFromXmlString = (panoramaData, keepImage = true) => {
            return new Promise((resolve) => {
                var _a;
                // Even though XML definition contains '%BASEDIR%/pano/...' we still need to add '/' at the end otherwise it gets lost
                const baseDir = panoramaData.baseDir.endsWith('/') ? panoramaData.baseDir : panoramaData.baseDir + '/';
                this._krpano.set('basedir', baseDir);
                // Url suffix is already sent starting with ? for a query string as urlSuffix contains SAS token for panorama files
                this._krpano.set('urlSuffix', `${panoramaData.urlSuffix}`);
                const functionReferenceName = FriendlyNameService.getFriendlyName(createGuid());
                const krpanoCurrent = this._krpano;
                krpanoCurrent.set(functionReferenceName, function () {
                    krpanoCurrent.set(functionReferenceName, null);
                    resolve();
                });
                return this._krpano.call(`loadxml(${(_a = panoramaData.xmlDefinition) !== null && _a !== void 0 ? _a : ''}, NULL,
          REMOVESCENES${keepImage ? '|KEEPIMAGE' : ''}, NULL, ${functionReferenceName}())`);
            });
        };
        this.loadScene = (sceneIndex, x, y, fov, blend) => {
            const vars = [
                ...(x ? [`view.hlookat=${x}`] : []),
                ...(y ? [`view.vlookat=${y}`] : []),
                ...(fov ? [`view.fov=${fov}`] : []),
            ];
            this._krpano.call(`loadscene(${sceneIndex},${vars.join('&')}, null, ${blend !== null && blend !== void 0 ? blend : 'BLEND(0)'})`);
        };
        this.registerHotspotOnClick = (hotspotName, handler) => this._krpano.set(`hotspot[${hotspotName}].onclick`, handler);
        this.getMinFov = () => this.isFlat() ? this.getFloatValue('view.fovmin') * 100 : this.getFloatValue('view.fovmin');
        this.getMaxFov = () => (this.isFlat() ? 100 : this.getFloatValue('view.fovmax'));
        this.getAverageFov = () => this.getMinFov() + (this.getMaxFov() - this.getMinFov()) / 2;
        this.getCurrentViewLookAt = () => {
            return {
                x: this._krpano.get('view.hlookat'),
                y: this._krpano.get('view.vlookat'),
                fov: this._krpano.get('view.fov'),
            };
        };
        this.setZoom = (fov) => {
            if (this.isFlat()) {
                this._krpano.set('view.fov', fov / 100);
            }
            else {
                this._krpano.set('view.fov', fov);
            }
        };
        this.getZoom = () => this.getFloatValue('view.fov');
        this.isFlat = () => this._krpano.get('image.type') === 'flat' || this._krpano.get('image.type') === 'cylinder';
        this.getSceneCount = () => this.getIntegerValue('scene.count');
        this.getSceneIndex = () => this.getIntegerValue('scene[get(xml.scene)].index', 0);
        this.lookTo = (lookTo, lookToQuick) => {
            const { xAxis: x, yAxis: y, fov } = lookTo;
            if (lookToQuick) {
                this._krpano.call(`lookat(${x},${y},${fov})`);
            }
            else if (this.isFlat()) {
                // cannot use "smooth" here, because in some cases it never reached the desired location, and got stuck.
                // Using tween is not as nice, but looks well in most cases. If two actions are too far from each other, can be a bit too fast.
                if (fov <= 1) {
                    // Ugly, but the easiest way to support centering when setting them NOT through slider, but from 'coordinates'
                    this._krpano.call(`lookto(${x}, ${y}, ${fov}, tween(easeinoutquad,1), true, true);`);
                }
                else {
                    this._krpano.call(`lookto(${x}, ${y}, ${fov / 100}, tween(easeinoutquad,1), true, true);`);
                }
            }
            else {
                this._krpano.call(`lookto(${x}, ${y}, ${fov}, smooth(120, 80, 150), true, true);`);
            }
        };
        this.removePolygonalArrowIndicator = (hotspotName) => {
            this._krpano.call(`removehotspot(${hotspotName}-indicator);`);
        };
        this.calculateMousePosition = () => {
            this._krpano.call('screentosphere(mouse.x,mouse.y,m_ath,m_atv);');
            const mx = this.getFloatValue('mouse.x');
            const my = this.getFloatValue('mouse.y');
            return this.screentosphere(mx, my);
        };
        this.changeCursorToCrosshair = () => this._krpano.set('cursors.standard', 'crosshair');
        this.changeCursorToDefault = () => this._krpano.set('cursors.standard', 'default');
        this.undoArrowPoint = (hotspotName, pointIndex) => {
            this._krpano.call(`hotspot[${hotspotName}].point.removearrayitem(${pointIndex});`);
        };
        this.disableHotspot = (hotspotName) => {
            this._krpano.set(`hotspot[${hotspotName}].enabled`, `false`);
        };
        this.updateScreen = () => this._krpano.call('updatescreen()');
        this._device = new DeviceKrpano(_krpano);
    }
    setPointYPosition(hotspotName, pointIndex, y) {
        this._krpano.set(`hotspot[${hotspotName}].point[${pointIndex}].atv`, y);
    }
    setPointXPosition(hotspotName, pointIndex, x) {
        this._krpano.set(`hotspot[${hotspotName}].point[${pointIndex}].ath`, x);
    }
}
